import {
    Component,
    computed,
    effect,
    EventEmitter,
    Inject,
    OnInit,
    Output,
    signal
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    DragDropModule,
    moveItemInArray
} from '@angular/cdk/drag-drop';
import { IImageDialogLogicService } from '@app/shared/interfaces/i-image-dialog-logic.service';
import { CbDialogContainerModule } from '@app/shared/components/dialog/dialog-container/dialog-container.module';
import { ToastService } from '@app/core/services/toast/toast.service';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { CbTruncateModule } from '@app/shared/pipe/truncate/truncate.module';
import { IUploadImageDto } from '@classictechsolutions/hubapi-transpiled-enums';
import { CbImageButtonModule } from '@app/shared/components/image-handlers/image-button/image-button.module';
import { LightboxComponent } from '@app/shared/components/image-handlers/lightbox/lightbox.component';

export interface MultiImageUploadData {
    entityId: number;
    dialogHeading: string;
    lotId: number;
    imageType: number;
    maxAllowed: number;
    existingImages: IUploadImageDto[];
    logicService: IImageDialogLogicService;
}

interface RejectedFile {
    file: File;
    reason: string;
}

@Component({
    selector: 'app-multi-image-upload-dialog',
    imports: [
        NgOptimizedImage,
        DragDropModule,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        CbDialogContainerModule,
        CbTruncateModule,
        CbImageButtonModule
    ],
    templateUrl: './multi-image-upload-dialog.component.html',
    styleUrls: ['./multi-image-upload-dialog.component.scss']
})
export class MultiImageUploadDialogComponent implements OnInit {
    public dialogHeading: string;
    public logicService: IImageDialogLogicService;
    public entityId: number;
    public imageType: number;
    public extension?: string = '.jpg,.jpeg,.png,.gif';
    public static readonly MIN_WIDTH = '300px';
    public readonly files = signal<IUploadImageDto[]>([]);
    public readonly isUploading = signal(false);
    private imageDimensions = new Map<string, { width: number; height: number }>();
    private readonly MAX_SIZE_MB = 6;
    public maxAllowed = 15;
    private readonly hideDelay = 10000;

    private readonly MAX_SIZE_BYTES = this.MAX_SIZE_MB * 1024 * 1024;
    private readonly NAME_REGEX = /\.(jpe?g|png|gif)$/i;
    @Output() public imagesUpdated = new EventEmitter<any>();
    public maxLength = 20;

    constructor(
        private readonly dialogRef: MatDialogRef<
            MultiImageUploadDialogComponent,
            IUploadImageDto[]
        >,
        @Inject(MAT_DIALOG_DATA) private readonly data: MultiImageUploadData,
        protected readonly toastService: ToastService,
        public readonly cbDialog: CbDialogService,
        private dialog: MatDialog
    ) {
        this.dialogHeading = data.dialogHeading;
        this.logicService = data.logicService;
        this.entityId = data.entityId;
        this.imageType = data.imageType;
        this.maxAllowed = data.maxAllowed;
        this.files.set(
            (data.existingImages ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder)
        );

        effect(() => this.dialogRef.updateSize(this.dialogWidth()));
    }

    private readonly dialogWidth = computed(() => {
        const fileCount = this.files().length;
        if (fileCount <= 1) {
            return '480px';
        }
        if (fileCount === 2) {
            return '600px';
        }
        return '900px';
    });

    public readonly thumbSize = computed(() => {
        const fileCount = this.files().length;
        return fileCount <= 1 ? { w: 400, h: 267 } : { w: 240, h: 160 };
    });

    public openImage(img: { url: string; name: string }): void {
        this.dialog.open(LightboxComponent, {
            data: {
                imgUrl: img.url,
                images: [img.url]
            },
            hasBackdrop: false,
            panelClass: 'cb-lightbox-dialog-container'
        });
    }

    public ngOnInit(): void {
        (async () => {
            await this.loadImageDimensions();
        })();
    }

    public getDimensions(imgId: string): string {
        const dim = this.imageDimensions.get(imgId);
        return dim ? `${dim.width}x${dim.height}` : '';
    }

    private async loadImageDimensions(): Promise<void> {
        const images = this.files();
        for (const img of images) {
            try {
                const dim = await this.getImageDimensionsFromUrl(img.url);
                this.imageDimensions.set(img.id, dim);
            } catch {
                console.warn(`Failed to get dimensions for image: ${img.name}`);
            }
        }
    }

    private getImageDimensionsFromUrl(url: string): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = reject;
            img.src = url;
        });
    }

    public confirmRemove(img: any): void {
        this.cbDialog.confirm({
            dialogHeading: 'Delete Image',
            message: `Are you sure you want to delete "${img.name}"?`,
            confirmed: () => {
                this.remove(img);
            }
        });
    }

    public cancel(): void {
        this.dialogRef.close(this.files());
    }

    public async onFilesSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;

        const files = this.getFiles(input);
        if (!files.length) {
            return;
        }

        if (this.files().length + files.length > this.maxAllowed) {
            this.showLimitReachedMessage();
            this.resetInput(input);
            return;
        }

        const { valid, rejected } = this.validateFiles(files);
        this.handleRejected(rejected);
        if (!valid.length) {
            this.resetInput(input);
            return;
        }

        this.isUploading.set(true);
        await this.uploadFiles(valid).finally(() => this.isUploading.set(false));

        this.persistSortOrder();
        this.resetInput(input);
    }

    private getFiles(input: HTMLInputElement): File[] {
        return input.files ? Array.from(input.files) : [];
    }

    private validateFiles(files: File[]): { valid: File[]; rejected: RejectedFile[] } {
        const valid: File[] = [];
        const rejected: RejectedFile[] = [];
        for (const f of files) {
            if (!this.NAME_REGEX.test(f.name) || !f.type.startsWith('image/')) {
                rejected.push({
                    file: f,
                    reason: `Wrong file type for ${f.name}. Please select a JPG, JPEG, PNG, GIF file.`
                });
            } else if (f.size > this.MAX_SIZE_BYTES) {
                rejected.push({
                    file: f,
                    reason: `${f.name} size exceeds ${this.MAX_SIZE_MB}MB limit. Please upload a smaller file.`
                });
            } else {
                valid.push(f);
            }
        }
        return { valid, rejected };
    }

    private handleRejected(rejected: RejectedFile[]): void {
        if (!rejected.length) {
            return;
        }
        for (const rejectedFile of rejected) {
            this.toastService.showToast(rejectedFile.reason, 'OK', this.hideDelay);
        }
    }

    private showLimitReachedMessage(): void {
        this.toastService.showToast(
            `Maximum of ${this.maxAllowed} images reached. Cannot upload more.`,
            'OK',
            this.hideDelay
        );
    }

    private async uploadFiles(files: File[]): Promise<void> {
        const tasks = files.map(f =>
            this.logicService
                .uploadImage(this.entityId, f, this.imageType)
                .toPromise()
                .then(dto => {
                    this.imagesUpdated.emit(dto.model);
                    this.addDto(dto.image);
                })
        );
        await Promise.all(tasks);
    }

    private addDto(dto: IUploadImageDto): void {
        const list = this.files();
        const max = list.length ? Math.max(...list.map(i => i.sortOrder)) + 1 : 1;
        dto.sortOrder = max;
        this.files.set([...list, dto]);
        this.getImageDimensionsFromUrl(dto.url)
            .then(dim => this.imageDimensions.set(dto.id, dim))
            .catch(() => console.warn(`Failed to get dimensions for image: ${dto.name}`));
    }

    public drop(event: CdkDragDrop<IUploadImageDto[]>): void {
        const arr = [...this.files()];
        moveItemInArray(arr, event.previousIndex, event.currentIndex);
        arr.forEach((f, i) => (f.sortOrder = i + 1));
        this.files.set(arr);
        this.persistSortOrder();
    }

    private resetInput(input: HTMLInputElement): void {
        input.value = '';
    }

    public remove(img: IUploadImageDto): void {
        this.logicService.deleteImage(this.entityId, img.id).subscribe(() => {
            const arr = this.files().filter(f => f.id !== img.id);
            arr.forEach((f, i) => (f.sortOrder = i + 1));
            this.files.set(arr);
            this.persistSortOrder();
        });
    }

    public persistSortOrder(): void {
        const orderedIds = this.files()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(f => f.id);
        this.logicService.updateImageSortOrder(this.entityId, orderedIds).subscribe(updatedDto => {
            this.imagesUpdated.emit(updatedDto);
        });
    }
}
